import { useState } from 'react';
import { useTheme } from '../App';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, Clock, MapPin, CheckCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { contactAPI } from '../lib/api';
import { toast } from 'sonner';

const strings = {
  en: {
    title: 'Contact Us',
    subtitle: 'Have a question or need help? We\'re here for you.',
    nameLabel: 'Your Name',
    emailLabel: 'Email Address',
    subjectLabel: 'Subject',
    messageLabel: 'Message',
    namePh: 'John Doe',
    emailPh: 'john@example.com',
    subjectPh: 'I need help with my appeal',
    messagePh: 'Describe your question or issue...',
    send: 'Send Message',
    sending: 'Sending...',
    successTitle: 'Message Sent!',
    successDesc: 'Thank you for contacting us. We\'ll respond within 24 hours.',
    sendAnother: 'Send Another Message',
    emailCard: 'Email Support',
    emailCardDesc: 'support@arbbio.com',
    responseCard: 'Response Time',
    responseCardDesc: 'Within 24 hours',
    officeCard: 'Office',
    officeCardDesc: 'European Union',
  },
  ar: {
    title: 'تواصل معنا',
    subtitle: 'هل لديك سؤال أو تحتاج مساعدة؟ نحن هنا لك.',
    nameLabel: 'اسمك',
    emailLabel: 'البريد الإلكتروني',
    subjectLabel: 'الموضوع',
    messageLabel: 'الرسالة',
    namePh: 'محمد أحمد',
    emailPh: 'mohammed@example.com',
    subjectPh: 'أحتاج مساعدة في طعني',
    messagePh: 'اكتب سؤالك أو مشكلتك...',
    send: 'إرسال الرسالة',
    sending: 'جارٍ الإرسال...',
    successTitle: 'تم الإرسال!',
    successDesc: 'شكراً لتواصلك معنا. سنرد خلال 24 ساعة.',
    sendAnother: 'إرسال رسالة أخرى',
    emailCard: 'البريد الإلكتروني',
    emailCardDesc: 'support@arbbio.com',
    responseCard: 'وقت الاستجابة',
    responseCardDesc: 'خلال 24 ساعة',
    officeCard: 'المكتب',
    officeCardDesc: 'الاتحاد الأوروبي',
  },
  de: {
    title: 'Kontakt',
    subtitle: 'Haben Sie eine Frage oder brauchen Hilfe? Wir sind für Sie da.',
    nameLabel: 'Ihr Name',
    emailLabel: 'E-Mail-Adresse',
    subjectLabel: 'Betreff',
    messageLabel: 'Nachricht',
    namePh: 'Max Mustermann',
    emailPh: 'max@beispiel.de',
    subjectPh: 'Ich brauche Hilfe mit meiner Berufung',
    messagePh: 'Beschreiben Sie Ihre Frage oder Ihr Problem...',
    send: 'Nachricht senden',
    sending: 'Wird gesendet...',
    successTitle: 'Nachricht gesendet!',
    successDesc: 'Vielen Dank für Ihre Kontaktaufnahme. Wir antworten innerhalb von 24 Stunden.',
    sendAnother: 'Weitere Nachricht senden',
    emailCard: 'E-Mail-Support',
    emailCardDesc: 'support@arbbio.com',
    responseCard: 'Antwortzeit',
    responseCardDesc: 'Innerhalb von 24 Stunden',
    officeCard: 'Büro',
    officeCardDesc: 'Europäische Union',
  },
};

export default function ContactPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { i18n } = useTranslation();
  const lang = ['en','ar','de'].includes(i18n.language) ? i18n.language : 'en';
  const s = strings[lang];

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const card = isDark
    ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]'
    : 'bg-white border border-gray-200 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-600';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const labelCl = `text-xs font-medium block mb-1.5 ${textM}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await contactAPI.send(form);
      setSubmitted(true);
      toast.success('Message sent!');
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    { icon: Mail, title: s.emailCard, desc: s.emailCardDesc, color: 'text-blue-500', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50' },
    { icon: Clock, title: s.responseCard, desc: s.responseCardDesc, color: 'text-purple-500', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50' },
    { icon: MapPin, title: s.officeCard, desc: s.officeCardDesc, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
  ];

  return (
    <div className={`min-h-screen transition-colors py-16 px-6 ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-14">
          <h1 className={`text-4xl md:text-5xl font-bold tracking-tight mb-4 ${textP}`} style={{ fontFamily: 'Outfit' }}>
            {s.title}
          </h1>
          <p className={`text-lg ${textS}`}>{s.subtitle}</p>
        </motion.div>

        {/* Info Cards */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {infoCards.map((ic, i) => (
            <div key={i} className={`${card} rounded-2xl p-6 flex items-start gap-4`}>
              <div className={`w-11 h-11 ${ic.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <ic.icon className={`w-5 h-5 ${ic.color}`} />
              </div>
              <div>
                <div className={`font-semibold text-sm mb-1 ${textP}`}>{ic.title}</div>
                <div className={`text-sm ${textS}`}>{ic.desc}</div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Form / Success */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={`${card} rounded-3xl p-8 md:p-10 max-w-2xl mx-auto`}>
          {submitted ? (
            <div className="text-center py-8">
              <div className={`w-20 h-20 ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              <h2 className={`text-2xl font-bold mb-3 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.successTitle}</h2>
              <p className={`mb-8 ${textS}`}>{s.successDesc}</p>
              <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                {s.sendAnother}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCl}>{s.nameLabel}</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={`${inputCl} rounded-xl h-11`} placeholder={s.namePh} />
                </div>
                <div>
                  <label className={labelCl}>{s.emailLabel}</label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={`${inputCl} rounded-xl h-11`} placeholder={s.emailPh} />
                </div>
              </div>
              <div>
                <label className={labelCl}>{s.subjectLabel}</label>
                <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className={`${inputCl} rounded-xl h-11`} placeholder={s.subjectPh} />
              </div>
              <div>
                <label className={labelCl}>{s.messageLabel}</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  rows={6}
                  placeholder={s.messagePh}
                  className={`w-full px-4 py-3 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors ${inputCl}`}
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-xl h-12 font-semibold">
                {loading ? (
                  <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{s.sending}</span>
                ) : (
                  <span className="flex items-center gap-2"><Send className="w-4 h-4" />{s.send}</span>
                )}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
